// SPDX-License-Identifier: BUSL-1.1

/**
 * Sign-in page — supports both API key auth and Better Auth session auth.
 *
 * Tab 1: API Key — for CLI agents and programmatic access
 * Tab 2: Email / Password — for human users via Better Auth
 * OAuth buttons: GitHub, Google (redirect to Better Auth endpoints)
 *
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { fetchAndStoreBearerJwt, useAuth } from '@/lib/auth';
import { API_BASE, AUTH_BASE } from '@/lib/api-client';
import { showError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// ─── API Key form ────────────────────────────────────────────────────────────

const apiKeySchema = z.object({
  apiKey: z.string().min(1, 'API key is required'),
});

type ApiKeyFormValues = z.infer<typeof apiKeySchema>;

// ─── Better Auth email/password form ─────────────────────────────────────────

const emailPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type EmailPasswordFormValues = z.infer<typeof emailPasswordSchema>;

// ─── OAuth icons (inline SVG — no extra deps) ────────────────────────────────

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12Z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62Z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" fill="#EA4335" />
    </svg>
  );
}

// ─── Health check ────────────────────────────────────────────────────────────

type HealthState = {
  loading: boolean;
  setupRequired: boolean | null;
  apiReachable: boolean;
};

function useHealthCheck() {
  const [health, setHealth] = useState<HealthState>({
    loading: true,
    setupRequired: null,
    apiReachable: true,
  });

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const res = await fetch(`${API_BASE}/health`);
        if (!cancelled) {
          if (!res.ok) {
            setHealth({ loading: false, setupRequired: null, apiReachable: true });
            return;
          }
          const data: { setup_required?: boolean } = await res.json();
          setHealth({
            loading: false,
            setupRequired: data.setup_required ?? null,
            apiReachable: true,
          });
        }
      } catch {
        if (!cancelled) {
          setHealth({ loading: false, setupRequired: null, apiReachable: false });
        }
      }
    }

    check();
    return () => { cancelled = true; };
  }, []);

  return health;
}

// ─── Sign In page ────────────────────────────────────────────────────────────

export function SignInPage() {
  const { login, loginWithSession } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'apikey' | 'email'>('email');
  const [serverError, setServerError] = useState('');
  const [apikeyError, setApikeyError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const health = useHealthCheck();

  const apiKeyForm = useForm<ApiKeyFormValues>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: { apiKey: '' },
  });

  const emailForm = useForm<EmailPasswordFormValues>({
    resolver: zodResolver(emailPasswordSchema),
    defaultValues: { email: '', password: '' },
  });

  // If already authenticated, redirect to home
  const { isAuthenticated, isLoading } = useAuth();
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  async function handleApiKeySubmit(values: ApiKeyFormValues) {
    setApikeyError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/configs`, {
        headers: { Authorization: `Bearer ${values.apiKey.trim()}` },
      });
      if (res.ok) {
        login(values.apiKey.trim());
        navigate('/');
      } else {
        const msg = 'Invalid API key. Please try again.';
        setApikeyError(msg);
        showError(msg);
      }
    } catch {
      const msg = 'Could not reach the server. Check your connection.';
      setApikeyError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleEmailPasswordSubmit(values: EmailPasswordFormValues) {
    setServerError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${AUTH_BASE}/api/auth/sign-in/email-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      });

      if (res.ok) {
        // Fetch the session to get user info and update React state
        const sessionRes = await fetch(`${AUTH_BASE}/api/auth/get-session`, {
          credentials: 'include',
        });
        if (sessionRes.ok) {
          // Better Auth returns `null` (HTTP 200) when there is no session.
          const { user } = (await sessionRes.json()) ?? {};
          if (user) {
            // Mint a Bearer JWT from cli-auth — required because cli.ceo and
            // cli-auth live on different origins, so the cli-auth cookie can't
            // be sent on cross-site requests to the cli.ceo API.
            await fetchAndStoreBearerJwt();
            loginWithSession(user);
          }
        }
        navigate('/');
      } else {
        const data = await res.json().catch(() => ({}));
        setServerError(data.error ?? 'Invalid email or password.');
        showError('Sign-in failed', data.error ?? 'Invalid email or password.');
      }
    } catch {
      setServerError('Could not reach the server. Check your connection.');
      showError('Network error', 'Could not reach the server.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950">
      <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-8">
        {/* Logo */}
        <div className="mb-8 font-mono text-xl font-bold tracking-tight text-neutral-100">
          cli.ceo<span className="animate-pulse text-emerald-400">_</span>
        </div>

        {/* API unreachable banner */}
        {!health.loading && !health.apiReachable && (
          <div className="mb-4 rounded border border-red-900/50 bg-red-950/30 px-4 py-3">
            <p className="text-sm font-medium text-red-400">
              Cannot reach the cli.ceo API. Is the server running?
            </p>
            <p className="mt-1 font-mono text-xs text-red-400/70">
              Try: docker compose up -d
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex rounded-lg border border-neutral-700 bg-neutral-800 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('email')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'bg-neutral-700 text-white'
                : 'text-neutral-400 hover:text-neutral-200'
            }`}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('apikey')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'apikey'
                ? 'bg-neutral-700 text-white'
                : 'text-neutral-400 hover:text-neutral-200'
            }`}
          >
            API Key
          </button>
        </div>

        {/* ── Email / Password tab ── */}
        {activeTab === 'email' && (
          <>
            <Form {...emailForm}>
              <form
                onSubmit={emailForm.handleSubmit(handleEmailPasswordSubmit)}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-400">Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="ada@example.com"
                          className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={emailForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-400">Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          placeholder="••••••••"
                          className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {serverError && (
                  <p className="rounded border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-400">
                    {serverError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 bg-emerald-600 text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </Form>

            {/* OAuth buttons */}
            <div className="mt-4 flex flex-col gap-2">
              <div className="relative flex items-center py-2">
                <div className="flex-1 border-t border-neutral-700" />
                <span className="mx-3 text-xs text-neutral-500">or continue with</span>
                <div className="flex-1 border-t border-neutral-700" />
              </div>
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  // After OAuth, cli-auth sets the session cookie on its own
                  // origin (cli-auth-staging.liftping.workers.dev) and then
                  // 302s back to callbackURL. The UI bootstraps the JWT from
                  // the cookie on app boot (see AuthProvider.init()).
                  const callbackURL = `${window.location.origin}/`;
                  window.location.href =
                    `${AUTH_BASE}/api/auth/sign-in/github?callbackURL=${encodeURIComponent(callbackURL)}`;
                }}
                className="flex items-center justify-center gap-2 border-neutral-700 bg-neutral-800 text-neutral-200 hover:bg-neutral-700 hover:text-white"
              >
                <GitHubIcon className="h-4 w-4" />
                GitHub
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  const callbackURL = `${window.location.origin}/`;
                  window.location.href =
                    `${AUTH_BASE}/api/auth/sign-in/google?callbackURL=${encodeURIComponent(callbackURL)}`;
                }}
                className="flex items-center justify-center gap-2 border-neutral-700 bg-neutral-800 text-neutral-200 hover:bg-neutral-700 hover:text-white"
              >
                <GoogleIcon className="h-4 w-4" />
                Google
              </Button>
            </div>

            <p className="mt-4 text-center text-sm text-neutral-500">
              No account?{' '}
              <Link to="/sign-up" className="text-emerald-400 hover:text-emerald-300">
                Create one
              </Link>
            </p>
          </>
        )}

        {/* ── API Key tab ── */}
        {activeTab === 'apikey' && (
          <>
            {!health.loading && health.apiReachable && health.setupRequired === true && (
              <div className="mb-4 rounded border border-blue-800/50 bg-blue-950/30 px-4 py-3">
                <p className="text-sm font-medium text-blue-300">
                  Welcome to cli.ceo! No API keys exist yet.
                </p>
                <p className="mt-1 text-xs text-blue-300/70">
                  Run <code className="rounded bg-blue-900/40 px-1 py-0.5 font-mono">./scripts/setup.sh</code> to
                  create your first API key, or see the Quick Start guide.
                </p>
              </div>
            )}

            <Form {...apiKeyForm}>
              <form
                onSubmit={apiKeyForm.handleSubmit(handleApiKeySubmit)}
                className="flex flex-col gap-4"
              >
                {/* Hidden username field for accessibility */}
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value="api-key"
                  readOnly
                  className="hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <FormField
                  control={apiKeyForm.control}
                  name="apiKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-400">API Key</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          placeholder="ceo_sk_..."
                          className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                          {...field}
                        />
                      </FormControl>
                      {!health.loading && health.apiReachable && health.setupRequired === false && (
                        <FormDescription className="text-neutral-500">
                          Enter your API key (starts with ceo_sk_). Need a key? Ask your admin
                          or run: <code className="font-mono">ceo keys create</code>
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {apikeyError && (
                  <p className="rounded border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-400">
                    {apikeyError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 bg-emerald-600 text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? 'Verifying...' : 'Sign in'}
                </Button>
              </form>
            </Form>
          </>
        )}
      </div>
    </div>
  );
}
